import { Location } from '@angular/common';
import { Component, OnInit, computed, effect, inject, input, linkedSignal, output, signal, viewChild } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { ColDef, GridOptions } from 'ag-grid-community';
import { sortBy } from 'lodash';
import { DateTime } from 'luxon';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { PanModalComponent } from '../../../components/pan-modal/pan-modal.component';
import { APPLICATION_TIMEZONES } from '../../../constants';
import { tryGetErrorMessage } from '../../../error.utils';
import { defaultTableDefOptions } from '../../../helpers';
import { LOCATION_MANAGEMENT_GENERAL_ROLES } from '../../../helpers/tabs';
import { ArchiveService } from '../../../service/archive.service';
import { BranchService } from '../../../service/branch.service';
import { LocationObjectService } from '../../../service/location-object.service';
import { LocationService } from '../../../service/location.service';
import { NotifyService } from '../../../service/notify.service';
import { UserLoginService } from '../../../service/user-login.service';
import { Branch, Kiosk, KioskTransaction, LocationAttribute, LocationCamera, LocationKeystore, LocationResponse } from '../../../types';
import { LocationObject, LocationObjectAssociationOf, Terminal } from '../../../types/location-object';
import { CellConnectionTypeComponent } from './cell-connection-type/cell-connection-type.component';
import { CellConnectionTypeTooltipComponent } from './cell-connection-type-tooltip/cell-connection-type-tooltip.component';
import { CellLocationEditCameraActionsComponent } from './cell-location-edit-camera-actions/cell-location-edit-camera-actions.component';
import { CellLocationEditKioskActionsComponent } from './cell-location-edit-kiosk-actions/cell-location-edit-kiosk-actions.component';
import { CellLocationEditTerminalActionsComponent } from './cell-location-edit-terminal-actions/cell-location-edit-terminal-actions.component';
import { InstallerCameraService } from './installer-camera.service';

type LocationCameraWithTerminals = Omit<LocationCamera, 'associations'> & {
  associations: LocationObjectAssociationOf<'CameraToTerminal', 'RhombusCamera', 'Terminal'>[];
};

@Component({
  selector: 'app-bl-management-location',
  templateUrl: './bl-management-location.component.html',
  styleUrls: ['./bl-management-location.component.scss'],
  standalone: false,
  providers: [InstallerCameraService],
})
export class BlManagementLocationComponent implements OnInit {
  private installerCameraService = inject(InstallerCameraService);
  private location = inject(Location);
  private router = inject(Router);

  private branchService = inject(BranchService);
  private locationService = inject(LocationService);
  private objectService = inject(LocationObjectService);
  private notifyService = inject(NotifyService);
  private archiveService = inject(ArchiveService);
  private userLogin = inject(UserLoginService);

  public branchId = input<number>();
  public locationId = input<number>();
  public isChild = input<boolean>(false);
  public selectLocationCamera = output<LocationCamera>();
  public isLoadingLocationData = signal<boolean>(false);
  public disableActions = signal<boolean>(false);

  public locationData = signal<LocationResponse | undefined>(undefined);
  public branchData = signal<Branch | undefined>(undefined);
  public snapshotDate = DateTime.local().minus({ days: 1 }).toISODate();
  public locationName = signal<string>('');
  public locationTimezone = signal<string>('');
  public locationExternalId = signal<string | null>(null);
  public locationAddCamera = signal<LocationCamera | undefined>(undefined);
  public selectedKiosk = signal<Kiosk | undefined>(undefined);
  public newKioskModal = viewChild<PanModalComponent>('newKioskModal');
  public dailyIncidentModal = viewChild<PanModalComponent>('dailyIncidentModal');
  public loadingKioskTransactions = signal<boolean>(false);
  public selectedKioskTransactions = signal<KioskTransaction[]>([]);

  public locationAttributeRA = signal<boolean>(false);
  public locationAttributeDL = signal<boolean>(false);
  public locationAttributeCC = signal<boolean>(false);
  public locationAttributeDIC = signal<boolean>(false);
  public locationAttributeMS = signal<boolean>(false);
  public locationAttributeAR = signal<boolean>(false);
  public locationAttributeLRCR = signal<boolean>(false);

  public newTerminalModal = viewChild<PanModalComponent>('newTerminalModal');
  public newTerminalName = signal<string>('');
  public newTerminalId = signal<string>('');

  public locationCardSubtitle = computed(() => {
    const data = this.locationData();
    if (!data) return '';

    return [`${data.timezone ?? 'No Timezone Set'}`, data.location_group_name ?? data.location_group_id, data.external_id]
      .filter(Boolean)
      .join(' | ');
  });

  public readonly allTimezones = signal<string[]>(APPLICATION_TIMEZONES);

  public allAddableCameras = signal<LocationCamera[]>([]);
  public allCameraIds = computed(() => this.cameras().map((c) => c.provider_camera_id));
  public unusedAddableCameras = computed(() => this.allAddableCameras().filter((c) => !this.allCameraIds().includes(c.provider_camera_id)));
  public sortedAddableCameras = computed(() => sortBy(this.unusedAddableCameras(), 'camera_name'));

  public allAddableKiosks = signal<Kiosk[]>([]);
  public branchAliases = computed(() => {
    const map = new Map<number, { branch_alias_id: number; display_name: string }>();
    for (const kiosk of this.allAddableKiosks()) {
      if (map.has(kiosk.branch_alias_id)) continue;
      map.set(kiosk.branch_alias_id, {
        branch_alias_id: kiosk.branch_alias_id,
        display_name: `${kiosk.data_source}: ${kiosk.branch_alias_name} (${kiosk.external_branch_id ?? '-'})`,
      });
    }
    return Array.from(map.values());
  });
  public selectedBranchAliasId = signal<number | undefined>(undefined);
  private kiosksByBranchAlias = computed(() => {
    const branchAliasId = this.selectedBranchAliasId();
    if (!branchAliasId) {
      return [];
    }
    return this.allAddableKiosks().filter((kiosk) => kiosk.branch_alias_id === branchAliasId);
  });
  public sortedAddableKiosks = computed(() => sortBy(this.kiosksByBranchAlias(), 'name'));

  public cameras = signal<LocationCameraWithTerminals[]>([]);
  public kiosks = signal<Kiosk[]>([]);
  public terminals = signal<LocationObject<'Terminal'>[]>([]);

  public viewCamera = signal<LocationCamera | undefined>(undefined);
  public viewCameraUrl = signal<SafeResourceUrl>(undefined);

  public canSave = computed(() => this.locationTimezone() && this.locationName());
  public showSnapshotDetailsButton = computed(() => this.locationAttributeMS());
  public showAssetRecoveryButton = computed(() => this.locationAttributeAR());

  public canAccessLocationEdit = computed(() =>
    LOCATION_MANAGEMENT_GENERAL_ROLES.some((role) => this.userLogin.currentProfileRoleList().includes(role)),
  );

  private dailyIncidentSummaryModal = viewChild<PanModalComponent>('dailyIncidentModal');
  private locationKeystore = signal<LocationKeystore | undefined>(undefined);
  private dailyIncidentRolesConfirmed = signal<boolean>(false);
  public dailyIncidentSelections = linkedSignal(() => this.locationKeystore()?.daily_incident_summary_roles ?? []);
  public dailyIncidentOptions = [
    {
      value: 'location-manager',
      text: 'Location Manager',
    },
    {
      value: 'guest',
      text: 'Guest',
    },
  ];
  private cameraDisplayCheckModal = viewChild<PanModalComponent>('cameraDisplayCheck');

  public readonly cameraColumns: ColDef<LocationCameraWithTerminals>[] = [
    {
      field: 'camera_name',
      headerName: 'Camera Name',
      cellDataType: 'text',
      minWidth: 200,
      maxWidth: 400,
      filter: 'agTextColumnFilter',
      sortable: true,
      sort: 'asc',
    },
    {
      headerName: 'Connection Type',
      cellDataType: 'text',
      minWidth: 150,
      maxWidth: 250,
      filter: 'agTextColumnFilter',
      sortable: true,
      valueGetter: (params) => {
        if (!params.data.connectivity_type) return 'Not Detected';
        if (params.data.isp_name) return `${params.data.connectivity_type} (${params.data.isp_name})`;
        return params.data.connectivity_type;
      },
      tooltipValueGetter: (params) => (params.data.isp_detected_at ? 'Connection details' : undefined),
      tooltipComponent: CellConnectionTypeTooltipComponent,
      cellRenderer: CellConnectionTypeComponent,
    },
    {
      headerName: 'Terminals',
      cellDataType: 'text',
      minWidth: 100,
      maxWidth: 400,
      filter: 'agTextColumnFilter',
      sortable: false,
      valueGetter: (params) => params.data.associations.map((c) => c.object2.attributes.provider_terminal_id).join(', '),
    },
    {
      headerName: '',
      suppressColumnsToolPanel: true,
      suppressKeyboardEvent: () => true,
      sortable: false,
      width: 200,
      cellClass: ['inline-flex', 'items-center', 'justify-end', 'gap-2'],
      cellRenderer: CellLocationEditCameraActionsComponent,
      cellRendererParams: {
        disableActions: this.disableActions,
        branchId: () => this.branchId(),
        previewCallback: (item: LocationCamera) => {
          this.viewCamera.set(item);
          this.loadViewCamera(item);
          this.cameraDisplayCheckModal()?.show();
        },
        deleteCallback: (item: LocationCamera) => {
          this.removeCamera(this.cameras().findIndex((c) => c.id === item.id));
        },
        editClicked: (item: LocationCamera) => {
          if (!this.isChild()) {
            this.router.navigate(['/home', 'manage', 'branch', this.branchId(), 'location', this.locationId(), 'camera', item.id]);
            return;
          }
          this.selectLocationCamera.emit(item);
        },
      },
    },
  ];

  public readonly cameraTable: GridOptions<LocationCameraWithTerminals> = {
    ...defaultTableDefOptions(),
    tooltipShowMode: 'standard',
    tooltipInteraction: true,
    popupParent: document.body,
  };

  public readonly kioskColumns: ColDef<Kiosk>[] = [
    {
      field: 'name',
      headerName: 'Kiosk Name',
      cellDataType: 'text',
      minWidth: 200,
      maxWidth: 400,
      filter: 'agTextColumnFilter',
      sortable: true,
      sort: 'asc',
    },
    {
      field: 'branch_alias_name',
      headerName: 'Organization',
      cellDataType: 'text',
      minWidth: 200,
      maxWidth: 400,
      filter: 'agTextColumnFilter',
      sortable: true,
      sort: 'asc',
    },
    {
      field: 'data_source',
      headerName: 'Data Source',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: true,
      sort: 'asc',
    },
    {
      field: 'external_id',
      headerName: 'External ID',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: true,
      sort: 'asc',
    },
    {
      field: 'unit_type',
      headerName: 'Unit Type',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: true,
      sort: 'asc',
    },
    {
      headerName: '',
      suppressColumnsToolPanel: true,
      suppressKeyboardEvent: () => true,
      sortable: false,
      width: 210,
      maxWidth: 210,
      cellClass: ['inline-flex', 'items-center', 'justify-end', 'gap-2'],
      cellRenderer: CellLocationEditKioskActionsComponent,
      cellRendererParams: {
        loadingTransactions: this.loadingKioskTransactions,
        kioskTransactions: this.selectedKioskTransactions,
        previewCallback: (item: Kiosk) => {
          this.selectedKiosk.set(item);
        },
        deleteCallback: (item: Kiosk) => {
          this.removeKiosk(this.kiosks().findIndex((k) => k.id === item.id));
        },
      },
    },
  ];

  public readonly kioskTable: GridOptions<Kiosk> = {
    ...defaultTableDefOptions(),
  };

  public readonly terminalColumns: ColDef<LocationObject<'Terminal'>>[] = [
    {
      headerName: 'Terminal Name',
      cellDataType: 'text',
      minWidth: 200,
      maxWidth: 400,
      filter: 'agTextColumnFilter',
      sortable: true,
      sort: 'asc',
      valueGetter: (params) => params.data.attributes.name,
    },
    {
      headerName: 'Terminal ID',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: true,
      sort: 'asc',
      valueGetter: (params) => params.data.attributes.provider_terminal_id,
    },
    {
      headerName: '',
      suppressColumnsToolPanel: true,
      suppressKeyboardEvent: () => true,
      sortable: false,
      width: 210,
      maxWidth: 210,
      cellClass: ['inline-flex', 'items-center', 'justify-end', 'gap-2'],
      cellRenderer: CellLocationEditTerminalActionsComponent,
      cellRendererParams: {
        deleteCallback: (item: LocationObject<'Terminal'>) => {
          this.deleteTerminal(this.terminals().findIndex((t) => t.id === item.id));
        },
      },
    },
  ];

  public readonly terminalTable: GridOptions<LocationObject<'Terminal'>> = {
    ...defaultTableDefOptions(),
  };

  constructor() {
    effect(() => {
      const addingCamera = this.locationAddCamera();
      if (!addingCamera) {
        this.viewCameraUrl.set(undefined);
        return;
      }

      this.loadCameraSnapshot({
        provider_branch_id: addingCamera.provider_branch_id,
        provider_camera_id: addingCamera.provider_camera_id,
      });
    });

    effect(() => {
      const selectedKiosk = this.selectedKiosk();
      if (!selectedKiosk) {
        return;
      }
      this.loadKioskTransactions();
    });

    effect(() => {
      const cameras = this.installerCameraService.cameras();
      this.allAddableCameras.set(cameras);
    });
  }

  ngOnInit() {
    if (this.canAccessLocationEdit()) {
      this.loadLocation();
      this.installerCameraService.setBranchId(this.branchId());
    } else {
      this.getLocation();
    }
  }

  private loadLocation() {
    this.isLoadingLocationData.set(true);
    forkJoin({
      availableKiosks: this.branchService.getBranchAvailableKiosks(this.branchId()),
      locationCameras: this.locationService.getLocationCameras({
        locationId: this.locationId(),
        options: { include_associations: true },
      }),
      terminals: this.objectService.getLocationObjectsOfType(this.locationId(), 'Terminal'),
      location: this.locationService.getLocation({
        locationId: this.locationId(),
        options: { include_kiosks: true },
      }),
      branch: this.branchService.getBranchById(this.branchId()),
    })
      .pipe(
        map(({ location, availableKiosks, locationCameras, terminals, branch }) => ({
          location: location,
          availableKiosks: availableKiosks?.items ?? [],
          locationCameras: locationCameras?.items ?? [],
          terminals: terminals?.items ?? [],
          branch,
        })),
        catchError(() =>
          of({
            location: undefined,
            availableKiosks: [] as Kiosk[],
            locationCameras: [] as LocationCamera[],
            terminals: [] as LocationObject<'Terminal'>[],
            branch: undefined,
          }),
        ),
        finalize(() => this.isLoadingLocationData.set(false)),
      )
      .subscribe(({ location, availableKiosks, locationCameras, terminals, branch }) => {
        this.allAddableKiosks.set(availableKiosks ?? []);
        this.branchData.set(branch);
        if (!location) return;
        this.locationData.set(location);
        this.locationName.set(location.name);
        this.locationTimezone.set(location.timezone);
        this.locationExternalId.set(location.external_id);

        this.cameras.set(
          locationCameras.map((lc) => ({
            ...lc,
            associations: lc.associations.filter((c) => c.type === 'CameraToTerminal') as LocationObjectAssociationOf<
              'CameraToTerminal',
              'RhombusCamera',
              'Terminal'
            >[],
          })),
        );
        this.kiosks.set(location.kiosks ?? []);
        this.terminals.set(terminals);
        this.locationAttributeRA.set(Boolean(location.attributes.has_restitution_agreement));
        this.locationAttributeDL.set(Boolean(location.attributes.is_dining_location));
        this.locationAttributeCC.set(Boolean(location.attributes.cellular_connection));
        this.locationAttributeDIC.set(Boolean(location.attributes.daily_incident_summary));
        this.locationAttributeMS.set(Boolean(location.attributes.market_snapshots_enabled));
        this.locationAttributeAR.set(Boolean(location.attributes.asset_recovery_enabled));
        this.locationAttributeLRCR.set(Boolean(location.attributes.low_risk_cancellation_review_enabled));
        this.locationKeystore.set(location.keystore);
        this.dailyIncidentSelections.set(location.keystore?.daily_incident_summary_roles ?? []);
      });
  }

  public addNewCamera() {
    this.disableActions.set(true);
    const providerCameraId = this.locationAddCamera()?.provider_camera_id;
    this.branchService
      .addCameraToLocation(this.branchId(), this.locationId(), {
        branch_camera_id: this.locationAddCamera()?.branch_camera_id,
        provider_camera_id: providerCameraId,
      })
      .pipe(finalize(() => this.locationAddCamera.set(undefined)))
      .subscribe({
        next: () => {
          this.notifyService.notify('Successfully added camera!');
          this.allAddableCameras.update((c) => c.filter((camera) => camera.provider_camera_id !== providerCameraId));
          this.loadCameras();

          this.disableActions.set(false);
        },
        error: () => {
          this.disableActions.set(false);
        },
      });
  }
  public removeCamera(index: number) {
    if (this.disableActions()) return;

    this.disableActions.set(true);
    const camera = this.cameras()[index];

    this.branchService
      .removeCameraFromLocation(this.branchId(), this.locationId(), camera.id)
      .pipe(finalize(() => this.disableActions.set(false)))
      .subscribe(() => {
        this.notifyService.notify('Successfully removed camera!');
        this.allAddableCameras.update((c) => {
          const existingCamera = c.find((cam) => cam.id === camera.id);
          if (existingCamera) return c;
          return [...c, camera];
        });
        this.loadCameras();
      });
  }

  public addNewKiosk() {
    if (!this.selectedKiosk()) return;
    this.disableActions.set(true);
    const newKiosk = this.selectedKiosk();
    this.branchService
      .addKioskToLocation(this.branchId(), this.locationId(), newKiosk)
      .pipe(
        finalize(() => {
          this.disableActions.set(false);
          this.newKioskModal()?.hide();
        }),
      )
      .subscribe((kiosk) => {
        this.notifyService.notify('Successfully added kiosk!');
        this.kiosks.update((c) => [...c, kiosk]);
        this.allAddableKiosks.update((kiosks) =>
          // remove this kiosk by name + branch_alias_name + data_source cause these don't have an id yet
          kiosks.filter(
            (item) =>
              !(
                item.name === newKiosk.name &&
                item.branch_alias_name === newKiosk.branch_alias_name &&
                item.external_branch_id === newKiosk.external_branch_id &&
                item.data_source === newKiosk.data_source &&
                item.external_id === newKiosk.external_id &&
                item.unit_type === newKiosk.unit_type
              ),
          ),
        );

        this.selectedKiosk.set(undefined);
      });
  }

  public removeKiosk(index: number) {
    if (this.disableActions()) return;

    this.disableActions.set(true);
    const kiosk = this.kiosks()[index];

    this.branchService
      .removeKioskFromLocation(this.branchId(), this.locationId(), kiosk.id)
      .pipe(finalize(() => this.disableActions.set(false)))
      .subscribe(() => {
        this.notifyService.notify('Successfully removed kiosk!');
        this.kiosks.update((k) => k.filter((kioskItem) => kioskItem.id !== kiosk.id));
        // the kiosk only comes back as addable when the catalogue still has it, so ask the server
        // instead of pushing it back here and showing something a reload would contradict
        this.reloadAvailableKiosks();
      });
  }

  private reloadAvailableKiosks() {
    this.branchService
      .getBranchAvailableKiosks(this.branchId())
      .pipe(catchError(() => of(undefined)))
      .subscribe((availableKiosks) => this.allAddableKiosks.set(availableKiosks?.items ?? []));
  }

  public addNewTerminal() {
    if (this.disableActions()) return;

    this.disableActions.set(true);
    const terminalToAdd = {
      provider_terminal_id: this.newTerminalId(),
      name: this.newTerminalName(),
    } satisfies Terminal;

    this.objectService
      .createLocationObject(this.locationId(), 'Terminal', terminalToAdd)
      .pipe(
        finalize(() => {
          this.disableActions.set(false);
          this.newTerminalModal()?.hide();
        }),
      )
      .subscribe({
        next: (result) => {
          this.terminals.update((curr) => [...curr, result]);
        },
        error: (err) => {
          this.notifyService.notify(tryGetErrorMessage(err));
        },
      });
  }

  public deleteTerminal(index: number) {
    if (this.disableActions()) return;

    this.disableActions.set(true);
    const terminalToDelete = this.terminals()[index];
    this.objectService
      .deleteLocationObject(this.locationId(), 'Terminal', terminalToDelete.id)
      .pipe(finalize(() => this.disableActions.set(false)))
      .subscribe({
        next: () => {
          this.notifyService.notify('Successfully removed terminal');
          this.terminals.update((curr) => curr.filter((t) => t.id !== terminalToDelete.id));
        },
        error: (err) => {
          this.notifyService.notify(tryGetErrorMessage(err));
        },
      });
  }

  public save() {
    this.disableActions.set(true);
    this.branchService
      .updateBranchLocation(this.branchId(), this.locationId(), {
        name: this.locationName(),
        timezone: this.locationTimezone(),
        external_id: this.locationExternalId(),
      })
      .pipe(finalize(() => this.disableActions.set(false)))
      .subscribe({
        next: () => {
          this.notifyService.notify('Successfully saved location!');
          this.loadLocation();
        },
        error: (err) => this.notifyService.notify(tryGetErrorMessage(err)),
      });
  }

  updateLocationAttribute(attribute: LocationAttribute, newValue: boolean) {
    this.disableActions.set(true);
    this.branchService
      .updateBranchLocationAttribute(+this.branchId(), +this.locationId(), attribute, newValue)
      .pipe(finalize(() => this.disableActions.set(false)))
      .subscribe(() => {});

    if (attribute === 'daily_incident_summary' && newValue) {
      this.dailyIncidentSummaryModal().show();
    }
  }

  archive() {
    this.disableActions.set(true);
    this.archiveService
      .archiveLocation(this.locationId())
      .pipe(finalize(() => this.disableActions.set(false)))
      .subscribe(() => {
        this.notifyService.notify('Successfully archived location!');
        this.location.back();
      });
  }

  public openNewKioskModal() {
    this.unsetSelectedKiosk();
    this.newKioskModal()?.show();
  }

  public unsetSelectedKiosk() {
    this.selectedKiosk.set(undefined);
    this.selectedKioskTransactions.set([]);
  }

  public clearTerminalValues() {
    this.newTerminalName.set('');
    this.newTerminalId.set('');
  }

  public loadViewCamera(camera: LocationCamera): void {
    if (!camera) {
      this.viewCamera.set(undefined);
      this.viewCameraUrl.set(undefined);
      return;
    }

    this.viewCamera.set(camera);
    this.viewCameraUrl.set(undefined);
    this.loadCameraSnapshot(camera);
  }

  private loadCameraSnapshot(camera: { provider_branch_id?: string; provider_camera_id?: string }) {
    this.viewCameraUrl.set(undefined);
    this.branchService.getCameraSnapshot(this.branchId(), camera.provider_branch_id, camera.provider_camera_id).subscribe({
      next: (blob) => {
        this.viewCameraUrl.set(URL.createObjectURL(blob));
      },
      error: () => this.viewCameraUrl.set('error'),
    });
  }

  private loadCameras() {
    this.locationService
      .getLocationCameras({
        locationId: this.locationId(),
        options: { include_associations: true },
        // bypass the client-side response cache so an add/remove is reflected immediately
        bypassCache: true,
      })
      .subscribe({
        next: (resp) => {
          this.cameras.set(
            resp.items.map((lc) => ({
              ...lc,
              associations: lc.associations.filter((c) => c.type === 'CameraToTerminal') as LocationObjectAssociationOf<
                'CameraToTerminal',
                'RhombusCamera',
                'Terminal'
              >[],
            })),
          );
        },
        error: (err) => {
          this.notifyService.notify(tryGetErrorMessage(err));
        },
      });
  }

  private loadKioskTransactions() {
    const selectedKiosk = this.selectedKiosk();
    if (!selectedKiosk || !selectedKiosk.name) return;
    this.loadingKioskTransactions.set(true);
    const request = this.branchService.getBranchKioskMostRecentTransactions(
      this.branchId(),
      undefined,
      selectedKiosk.name,
      selectedKiosk.branch_alias_name,
    );
    request.pipe(finalize(() => this.loadingKioskTransactions.set(false))).subscribe({
      next: (response) => {
        this.selectedKioskTransactions.set(response.items);
      },
      error: () => this.selectedKioskTransactions.set([]),
    });
  }

  public unloadViewCamera() {
    this.viewCamera.set(undefined);
    this.viewCameraUrl.set(undefined);
  }

  public confirmDailyIncidentDetails() {
    if (!this.locationData()) return;
    this.dailyIncidentRolesConfirmed.set(true);
    this.locationService
      .patchLocationKeystore(this.locationId(), {
        daily_incident_summary_roles: this.dailyIncidentSelections(),
      })
      .subscribe({
        next: (keystoreResponse) => {
          this.locationData.update((loc) => {
            return {
              ...loc,
              keystore: keystoreResponse,
            };
          });
          this.locationKeystore.set(keystoreResponse);
          this.notifyService.notify('Successfully updated daily incident summary roles');
          this.dailyIncidentModal().hide();
        },
        error: (err) => {
          this.notifyService.notify(tryGetErrorMessage(err));
          this.dailyIncidentModal().hide();
        },
      });
  }

  public onDailyIncidentModalOpen() {
    this.dailyIncidentRolesConfirmed.set(false);
  }
  public onDailyIncidentModalClose() {
    if (!this.dailyIncidentRolesConfirmed()) {
      this.dailyIncidentSelections.set(this.locationKeystore()?.daily_incident_summary_roles ?? []);
    }
  }

  private getLocation() {
    this.isLoadingLocationData.set(true);
    this.locationService
      .getLocation({
        locationId: this.locationId(),
        options: { include_kiosks: false },
      })
      .pipe(
        catchError(() => of(undefined)),
        finalize(() => this.isLoadingLocationData.set(false)),
      )
      .subscribe((location) => {
        if (!location) return;
        this.locationData.set(location);
        this.locationAttributeMS.set(Boolean(location.attributes.market_snapshots_enabled));
        this.locationAttributeAR.set(Boolean(location.attributes.asset_recovery_enabled));
      });
  }
}
